import { Link } from 'react-router-dom'
import { Card } from '@diwata/ironvale'
import { useUI } from '@/store/ui'

// "Begin here" — the guided first steps through the loop. Home introduced it as a first-run
// strip; the founder wants it to persist across pages, so its home is now the control plane's
// ◈ (needs-you) tab, which rides every page. Home keeps a copy only while the verse is
// domainless. Dismissing either copy dismisses both (one persisted flag in the ui store).
//
// TODO(awards): this card is the future hook for awards/badges — step completion should turn
// into progression marks rather than a static list. Keep it light until that lands.

const STEPS = [
  ['Create a domain', '/domains?create=1', 'pick a template and see exactly what it bundles'],
  ['Watch its springs', '/pipelines', 'pipelines feed the lake; your rules decide what signals'],
  ['Judge what asks', '/needs', 'signals and proposals wait in ◈ — nothing acts without you'],
  ['Track your first star', '/ledger', 'a lake row becomes yours with one click'],
] as const

function StepList({ compact }: { compact?: boolean }) {
  return (
    <div style={{ display: 'grid', gap: 6, fontSize: `calc(${compact ? 12 : 13}px * var(--ae-text-scale, 1))` }}>
      {STEPS.map(([label, to, hint], i) => (
        <div key={to}>
          <span className="dw-inbox-detail" style={{ marginRight: 8, fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>
          <Link className="dw-link" to={to}>{label}</Link>
          <span className="dw-inbox-detail"> — {hint}</span>
        </div>
      ))}
    </div>
  )
}

/** Home's inline card — first-run only (a domainless verse). Moved out of Dashboard.tsx. */
export function BeginHereCard() {
  const dismissed = useUI((s) => s.beginHereDismissed)
  const setDismissed = useUI((s) => s.setBeginHereDismissed)
  if (dismissed) return null
  return (
    <Card className="dw-accent">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div className="dw-rail-section-title">Begin here</div>
        <button type="button" className="dw-back-link" style={{ padding: 0 }} onClick={() => setDismissed(true)}>
          dismiss
        </button>
      </div>
      <StepList />
      <div className="dw-inbox-detail" style={{ marginTop: 8 }}>
        Lost later? <Link className="dw-link" to="/map">How it connects</Link> is always in the nav.
        It also waits in ◈ on every page.
      </div>
    </Card>
  )
}

/**
 * The persistent copy: a compact card at the TOP of the ◈ needs-you tab, on every page.
 * Minimizable (collapses to its one-line header) and dismissible — both persisted.
 */
export function BeginHereRail() {
  const dismissed = useUI((s) => s.beginHereDismissed)
  const setDismissed = useUI((s) => s.setBeginHereDismissed)
  const collapsed = useUI((s) => s.beginHereCollapsed)
  const setCollapsed = useUI((s) => s.setBeginHereCollapsed)
  if (dismissed) return null
  return (
    <div
      style={{
        border: '1px solid var(--ae-color-border)',
        borderRadius: 10,
        padding: '8px 10px',
        marginBottom: 14,
        background: 'var(--ae-color-bg-muted)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <button
          type="button"
          aria-expanded={!collapsed}
          className="dw-rail-section-title"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginBottom: 0, flex: 1, textAlign: 'left' }}
          onClick={() => setCollapsed(!collapsed)}
        >
          Begin here {collapsed ? '▸' : '▾'}
        </button>
        <button type="button" className="dw-back-link" style={{ padding: 0 }} onClick={() => setDismissed(true)}>
          dismiss
        </button>
      </div>
      {!collapsed && (
        <div style={{ marginTop: 8 }}>
          <StepList compact />
        </div>
      )}
    </div>
  )
}
